import type { BlockStackAlign, BlockStackPadding } from '@/lib/blocks/layout/block-stack-layout';
import { pickStringUnion, readString } from '@/lib/prop-utils';
import type { ImageValue } from '@/types/image';
import type { VideoValue } from '@/types/video';

export const SECTION_BACKGROUND_TYPE_VALUES = ['color', 'image', 'video'] as const;
export const SECTION_BACKGROUND_SPACING_VALUES = ['none', 'sm', 'md', 'lg'] as const;
export const SECTION_BACKGROUND_VERTICAL_ALIGN_VALUES = ['top', 'center', 'bottom'] as const;
export const SECTION_BACKGROUND_CONTENT_TONE_VALUES = ['light', 'dark', 'siteText'] as const;
export const SECTION_BACKGROUND_INSET_SIZE_VALUES = ['sm', 'md', 'lg'] as const;

export type SectionBackgroundType = (typeof SECTION_BACKGROUND_TYPE_VALUES)[number];
export type SectionBackgroundSpacing = (typeof SECTION_BACKGROUND_SPACING_VALUES)[number];
export type SectionBackgroundVerticalAlign =
  (typeof SECTION_BACKGROUND_VERTICAL_ALIGN_VALUES)[number];
export type SectionBackgroundContentTone = (typeof SECTION_BACKGROUND_CONTENT_TONE_VALUES)[number];
export type SectionBackgroundInsetSize = (typeof SECTION_BACKGROUND_INSET_SIZE_VALUES)[number];

// Render-time safety floors for the string-union props, used only when a stored value is missing
// or invalid.
const SECTION_BACKGROUND_SPACING_FALLBACK: SectionBackgroundSpacing = 'md';
const SECTION_BACKGROUND_VERTICAL_ALIGN_FALLBACK: SectionBackgroundVerticalAlign = 'center';
const SECTION_BACKGROUND_INSET_SIZE_FALLBACK: SectionBackgroundInsetSize = 'sm';

export function readSectionBackgroundType(value: unknown): SectionBackgroundType | undefined {
  const raw = readString(value);
  return raw && (SECTION_BACKGROUND_TYPE_VALUES as readonly string[]).includes(raw)
    ? (raw as SectionBackgroundType)
    : undefined;
}

export function parseSectionBackgroundSpacing(value: unknown): SectionBackgroundSpacing {
  return pickStringUnion(
    readString(value),
    SECTION_BACKGROUND_SPACING_VALUES,
    SECTION_BACKGROUND_SPACING_FALLBACK,
  );
}

export function parseSectionBackgroundVerticalAlign(
  value: unknown,
): SectionBackgroundVerticalAlign {
  return pickStringUnion(
    readString(value),
    SECTION_BACKGROUND_VERTICAL_ALIGN_VALUES,
    SECTION_BACKGROUND_VERTICAL_ALIGN_FALLBACK,
  );
}

export function parseSectionBackgroundInsetSize(value: unknown): SectionBackgroundInsetSize {
  return pickStringUnion(
    readString(value),
    SECTION_BACKGROUND_INSET_SIZE_VALUES,
    SECTION_BACKGROUND_INSET_SIZE_FALLBACK,
  );
}
export interface BlockSectionProps {
  blockAlign: BlockStackAlign;
  blockPadding: BlockStackPadding;
  backgroundType: SectionBackgroundType | null;
  backgroundColor: string | null;
  backgroundImage: ImageValue | null;
  backgroundVideo: VideoValue | null;
  backgroundOverlay: number | null;
  backgroundOverlayColor: string | null;
  backgroundFocusX: number | null;
  backgroundFocusY: number | null;
  backgroundFullWidth: boolean;
  backgroundSpacing: SectionBackgroundSpacing;
  backgroundVerticalAlign: SectionBackgroundVerticalAlign;
  backgroundInset: boolean;
  backgroundInsetSize: SectionBackgroundInsetSize;
}
